import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import {
    Button,
    ButtonProps
} from "@/libraries/design/components/button/src/button";
import {
    Image,
    ImageProps
} from "@/libraries/design/components/image/src/image";
import { Row } from "@/libraries/wpl/foundations/src";

export type HeroButtonProps = {
    text: string;
} & ButtonProps;

export type HeroLayoutProps = "image-left" | "image-right";

export type HeroProps = {
    heading: string;
    subheading?: string;
    callToActionProps?: HeroButtonProps;
    layout?: HeroLayoutProps;
    imageProps?: ImageProps;
    size?: "large" | "regular";
};

export const Hero = ({
    heading,
    subheading,
    callToActionProps,
    layout = "image-right",
    imageProps,
    size = "regular"
}: HeroProps) => {
    const { text: buttonText, ...buttonProps } =
        (callToActionProps as HeroButtonProps) ?? {};
    const isLarge = size === "large";

    return (
        <section>
            <Row layout="12">
                <div css={gridStyle}>
                    <div css={layout === "image-left" && imageStartStyle}>
                        <h1
                            css={headingStyle}
                            className={isLarge ? "superhero" : undefined}
                        >
                            {heading}
                        </h1>
                        {subheading && (
                            <p
                                className={isLarge ? "body-lg" : "body-md"}
                                css={bodyStyle}
                            >
                                {subheading}
                            </p>
                        )}
                        {buttonText && buttonProps && (
                            <Button
                                {...buttonProps}
                                size="large"
                                css={buttonStyle}
                            >
                                {buttonText}
                            </Button>
                        )}
                    </div>
                    {imageProps && (
                        <Image
                            maxPossibleWidth={700}
                            src={imageProps.src}
                            alt={imageProps.alt ?? ""}
                            loading="eager"
                            width={imageProps.width}
                            height={imageProps.height}
                        />
                    )}
                </div>
            </Row>
        </section>
    );
};

export default Hero;

const gridStyle = css({
    display: "grid",
    gap: token("space.300"),
    paddingBlockStart: token("space.500"),
    paddingBlockEnd: token("space.400"),
    textAlign: "center",
    alignItems: "center",

    [media.above.sm]: {
        gridTemplateColumns: "1fr 1fr",
        gridAutoFlow: "dense",
        gap: token("space.400"),
        textAlign: "start"
    },
    [media.only.sm]: {
        paddingBlockStart: token("space.600"),
        paddingBlockEnd: token("space.500")
    },
    [media.above.md]: {
        paddingBlockStart: token("space.800"),
        paddingBlockEnd: token("space.600")
    }
});

const headingStyle = css({
    margin: token("space.0")
});

const imageStartStyle = css({
    [media.above.sm]: {
        gridColumn: 2
    }
});

const bodyStyle = css({
    marginBlockEnd: token("space.0")
});
const buttonStyle = css({
    marginBlockStart: token("space.400")
});
